import { useCallback, useEffect } from 'react';
import { useAppSelector } from '@/store/hooks';
import { setErrorLanguage } from '@/utils/friendlyError';
import { translations, TranslationSection } from './translations';

/**
 * Makes a component redraw when the language changes. trText reads the language when it is called, so on its own
 * it never tells React anything changed; a component (or a memo'd card) that only calls trText would otherwise keep
 * the old language until something else redraws it.
 */
export function useLanguage(): string {
  return useAppSelector((state) => state.preferences.language) || 'en';
}

export function useTranslation() {
  const language = useAppSelector((state) => state.preferences.language) || 'en';
  // Error toasts built outside React (src/utils/friendlyError.ts) follow the app language too.
  useEffect(() => {
    setErrorLanguage(language === 'bn' ? 'bn' : 'en');
  }, [language]);
  const isBn = language === 'bn';
  const dict = translations[language] || translations.en;

  const t = useCallback(
    <S extends string = string, K extends string = string>(
      section: S,
      key: K,
      fallback?: string,
    ): string => {
      const sectionObj = (dict as any)?.[section] as Record<string, string> | undefined;
      const fallbackObj = (translations.en as any)?.[section] as Record<string, string> | undefined;
      return sectionObj?.[key as string] ?? fallbackObj?.[key as string] ?? fallback ?? (key as string);
    },
    [dict],
  );

  const tText = useCallback(
    (en: string, bn: string): string => {
      return isBn ? bn : en;
    },
    [isBn],
  );

  return {
    t,
    tText,
    language,
    isBn,
  };
}
